// dashboard/src/components/AdminReceiptTemplates.jsx
// Allows master_admin / admin to upload custom background images for each receipt type.
import React, { useEffect, useRef, useState } from 'react';
import { API_BASE } from '../api';

const TYPES = [
  { key: 'online',  label: 'Online (PayU / PG)',  defaultImg: null },
  { key: 'cash',    label: 'Cash / UPI',           defaultImg: null },
  { key: 'cheque',  label: 'Cheque',               defaultImg: null },
  { key: 'upi',     label: 'UPI (offline)',         defaultImg: null },
];

const AdminReceiptTemplates = ({ readOnly = false }) => {
  const token = localStorage.getItem('token');
  const [templates, setTemplates]   = useState({});   // { online: 'filename.jpg', ... }
  const [uploading, setUploading]   = useState({});   // { online: true/false, ... }
  const [deleting, setDeleting]     = useState({});
  const [message, setMessage]       = useState('');
  const fileRefs = useRef({});

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/receipt-templates`);
      const data = await res.json();
      setTemplates(data);
    } catch { /* silently ignore */ }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleUpload = async (type, file) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [type]: true }));
    setMessage('');
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch(`${API_BASE}/api/receipt-templates/${type}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setMessage(`✅ Template for "${type}" updated successfully.`);
      fetchTemplates();
    } catch (err) {
      setMessage(`❌ ${err.message}`);
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
      if (fileRefs.current[type]) fileRefs.current[type].value = '';
    }
  };

  const handleDelete = async (type) => {
    if (!window.confirm(`Remove custom template for "${type}"? The default image will be used.`)) return;
    setDeleting(prev => ({ ...prev, [type]: true }));
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/api/receipt-templates/${type}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      setMessage(`✅ Custom template for "${type}" removed.`);
      fetchTemplates();
    } catch (err) {
      setMessage(`❌ ${err.message}`);
    } finally {
      setDeleting(prev => ({ ...prev, [type]: false }));
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2">Receipt Template Management</h2>
      <p className="text-gray-500 text-sm mb-6">
        Upload a custom background image for each receipt type. JPG/PNG, max 15 MB.
        If no custom template is uploaded, the default static image is used.
      </p>

      {message && (
        <div className={`mb-4 p-3 rounded text-sm ${message.startsWith('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {TYPES.map(({ key, label }) => {
          const filename = templates[key];
          const imgUrl   = filename ? `${API_BASE}/uploads/receiptTemplates/${filename}` : null;

          return (
            <div key={key} className="border rounded-lg p-4 bg-white shadow-sm">
              <h3 className="font-semibold text-lg mb-3">{label}</h3>

              {/* Current template preview */}
              <div className="mb-3 bg-gray-100 rounded overflow-hidden flex items-center justify-center" style={{ height: 160 }}>
                {imgUrl ? (
                  <img
                    src={imgUrl}
                    alt={`${label} template`}
                    className="object-contain max-h-full max-w-full"
                  />
                ) : (
                  <span className="text-gray-400 text-sm">Using default static image</span>
                )}
              </div>

              {/* Status badge */}
              <p className="text-xs mb-3">
                {filename
                  ? <span className="text-green-700 font-medium">Custom template active</span>
                  : <span className="text-gray-500">No custom template — default in use</span>
                }
              </p>

              {!readOnly && (
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Upload button */}
                  <label className="cursor-pointer bg-primary text-white text-sm px-3 py-1.5 rounded hover:bg-secondary transition-colors">
                    {uploading[key] ? 'Uploading…' : 'Upload New'}
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      className="hidden"
                      ref={el => fileRefs.current[key] = el}
                      disabled={uploading[key]}
                      onChange={e => handleUpload(key, e.target.files[0])}
                    />
                  </label>

                  {/* Remove custom template button */}
                  {filename && (
                    <button
                      onClick={() => handleDelete(key)}
                      disabled={deleting[key]}
                      className="text-sm px-3 py-1.5 rounded border border-red-400 text-red-600 hover:bg-red-50 transition-colors"
                    >
                      {deleting[key] ? 'Removing…' : 'Remove Custom'}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminReceiptTemplates;
